<template>
  <div class="report-detail-overlay" @click="closeModal">
    <div class="report-detail-modal" @click.stop>
      <div class="modal-header">
        <h2>Bug Report</h2>
        <button class="close-btn" @click="closeModal">&times;</button>
      </div>

      <div class="modal-content">
        <!-- Summary -->
        <div class="info-section">
          <h3>{{ report.name }}</h3>
          <div class="info-row">
            <span class="label">Type:</span>
            <span class="value"
              ><span class="type-pill">{{ report.type }}</span></span
            >
          </div>
          <div class="info-row">
            <span class="label">Submitted:</span>
            <span class="value">{{ formatDate(report.createdAt) }}</span>
          </div>
          <div class="info-row">
            <span class="label">User ID:</span>
            <span class="value">{{ report.userId || 'Anonymous' }}</span>
          </div>
          <div class="info-row">
            <span class="label">Report ID:</span>
            <span class="value mono">{{ report.id }}</span>
          </div>
        </div>

        <!-- Description -->
        <div class="info-section">
          <h3>Description</h3>
          <p class="description">{{ report.text }}</p>
        </div>

        <!-- Metadata -->
        <div class="info-section">
          <h3>Metadata</h3>
          <div v-if="metadataEntries.length" class="metadata-table">
            <div v-for="[key, value] in metadataEntries" :key="key" class="metadata-row">
              <span class="metadata-key">{{ key }}</span>
              <span class="metadata-value mono">{{ formatValue(value) }}</span>
            </div>
          </div>
          <p v-else class="empty">No metadata.</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import type { BugReport } from '../client.js';

const props = defineProps<{ report: BugReport }>();

const emit = defineEmits<{ close: [] }>();

const metadataEntries = computed(() => Object.entries(props.report.metadata ?? {}));

const formatDate = (date: Date) => new Date(date).toLocaleString();

const formatValue = (value: unknown): string => {
  if (value === null || value === undefined) return '—';
  if (typeof value === 'object') return JSON.stringify(value, null, 2);
  return String(value);
};

const closeModal = () => emit('close');
</script>

<style scoped>
.report-detail-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
  z-index: 1000;
}

.report-detail-modal {
  background: white;
  border-radius: 0.5rem;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.2);
  width: 100%;
  max-width: 640px;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 1.5rem;
  border-bottom: 1px solid #e5e7eb;
}

.modal-header h2 {
  margin: 0;
  color: #1f2937;
  font-size: 1.25rem;
}

.close-btn {
  background: none;
  border: none;
  font-size: 1.75rem;
  line-height: 1;
  color: #6b7280;
  cursor: pointer;
}

.close-btn:hover {
  color: #1f2937;
}

.modal-content {
  padding: 1.5rem;
  overflow-y: auto;
}

.info-section {
  margin-bottom: 1.5rem;
}

.info-section:last-child {
  margin-bottom: 0;
}

.info-section h3 {
  margin: 0 0 0.75rem;
  color: #1f2937;
  font-size: 1rem;
  border-bottom: 1px solid #e5e7eb;
  padding-bottom: 0.5rem;
}

.info-row {
  display: flex;
  gap: 0.75rem;
  padding: 0.25rem 0;
}

.label {
  flex: 0 0 110px;
  color: #6b7280;
  font-size: 0.875rem;
}

.value {
  color: #1f2937;
  font-size: 0.875rem;
  word-break: break-word;
}

.mono {
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 0.8125rem;
}

.type-pill {
  display: inline-block;
  background: #e0e7ff;
  color: #3730a3;
  padding: 0.125rem 0.5rem;
  border-radius: 9999px;
  font-size: 0.75rem;
  font-weight: 500;
}

.description {
  margin: 0;
  white-space: pre-wrap;
  word-break: break-word;
  color: #1f2937;
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 0.375rem;
  padding: 0.75rem;
  font-size: 0.9375rem;
  line-height: 1.5;
}

.metadata-table {
  border: 1px solid #e5e7eb;
  border-radius: 0.375rem;
  overflow: hidden;
}

.metadata-row {
  display: flex;
  gap: 0.75rem;
  padding: 0.5rem 0.75rem;
  border-bottom: 1px solid #f3f4f6;
}

.metadata-row:last-child {
  border-bottom: none;
}

.metadata-row:nth-child(even) {
  background: #f9fafb;
}

.metadata-key {
  flex: 0 0 160px;
  color: #6b7280;
  font-size: 0.8125rem;
  font-weight: 500;
  word-break: break-word;
}

.metadata-value {
  flex: 1;
  color: #1f2937;
  word-break: break-word;
  white-space: pre-wrap;
}

.empty {
  margin: 0;
  color: #6b7280;
  font-style: italic;
  font-size: 0.875rem;
}

@media (max-width: 768px) {
  .info-row,
  .metadata-row {
    flex-direction: column;
    gap: 0.125rem;
  }

  .label,
  .metadata-key {
    flex: none;
  }
}
</style>
